import {
  getTrpcRetryAfterMs,
  isTrpcAuthNotReadyError,
  isTrpcFetchAbortError,
  isTrpcRateLimitError,
  isTrpcTokenRefreshError,
  isTrpcTransientInfraError,
  isTrpcUnauthorizedError,
  MAX_RETRY_AFTER_MS,
} from './classification';

/**
 * Attempts a query gets for a transient failure at the browser's own hop —
 * network teardown, an infra 5xx, or auth still settling.
 */
export const DEFAULT_QUERY_RETRY_COUNT = 3;

/**
 * Attempts a query gets for a 429. Lower than the default because each attempt
 * waits out a `retryAfterSec` window, so the wall-clock cost per attempt is an
 * order of magnitude higher.
 */
export const RATE_LIMIT_RETRY_COUNT = 2;

const RETRY_BASE_DELAY_MS = 500;
const RETRY_MAX_DELAY_MS = 5000;
const RATE_LIMIT_JITTER_MS = 1000;

/**
 * Exponential backoff with **equal jitter**. Same formula as `computeBackoffMs`
 * in `packages/data-layer-client/src/http-client.ts` — `min(base · 2^attempt,
 * cap) · (0.5 + rand · 0.5)` — replicated rather than imported because that is
 * a Node package the browser bundle cannot pull in.
 *
 * Equal, not full: the multiplier's floor is 0.5, so the delay lands in
 * `[window/2, window]` and half of it is fixed. Full jitter would be `rand ·
 * window`, i.e. `[0, window]`, which can retry almost immediately. Worth being
 * exact about — the floor is what bounds the worst case the tests assert, and
 * "full jitter" would imply a lower bound of zero that this does not have.
 *
 * `attempt` is 0-based, so the first retry waits ~`RETRY_BASE_DELAY_MS`.
 */
const computeBackoffMs = (attempt: number): number => {
  const capped = Math.min(
    RETRY_BASE_DELAY_MS * 2 ** attempt,
    RETRY_MAX_DELAY_MS
  );

  return Math.round(capped * (0.5 + Math.random() * 0.5));
};

/**
 * Default `retry` for every query, wired into each app's `makeQueryClient`
 * (`packages/web/src/utils/trpc.ts`,
 * `packages/third-party-portal/src/utils/trpc.ts`). Mutations are never retried
 * at all — see the `mutations` defaults there.
 *
 * **Each hop retries only its own transient failures, once (COI-159).** The
 * tRPC tier already retries its data-layer hop 3× for idempotent methods
 * (`packages/data-layer-client/src/http-client.ts`), so a data-layer fault that
 * survives those arrives here as a settled `INTERNAL_SERVER_ERROR`. Retrying it
 * again multiplies 4 attempts into 16 against an upstream that is already
 * failing — the amplification the ENG-616 e2e incident is about
 * (`packages/e2e/config/apiClient.ts`). So a deterministic failure — 4xx, 500,
 * anything the server decided on purpose — is terminal here.
 *
 * What IS transient at the browser's own hop:
 *
 * - **429** (COI-882), but only when the limiter says its window clears within
 *   {@link MAX_RETRY_AFTER_MS}. Previously never retried, because a retry spent
 *   another point against the same budget and kept the user pinned over the
 *   limit. It is safe now only because the wait is the limiter's own
 *   `retryAfterSec`, honoured in full, so the retry goes out after the window
 *   has rolled over and does not spend a point against the budget that rejected
 *   it. A longer `retryAfterSec` than we are willing to wait is therefore
 *   terminal rather than retried-early — retrying early is the COI-882 bug.
 *   Capped at two attempts.
 * - **502/503/504/408 with no tRPC envelope** — the edge failed and nobody has
 *   retried it yet. The same statuses *with* an envelope are terminal: the tRPC
 *   tier already spent four attempts on them (see `isTrpcTransientInfraError`).
 * - **Fetch-level teardown** — a connection that died mid-flight.
 * - **Auth not ready** — a start-up race that resolves itself (COI-897).
 *
 * A 401 and a failed silent refresh are both terminal: a `/logout` navigation
 * is already in flight (`./authHeaders`, `./authRedirectLink`), and no number
 * of retries can make the token the client holds work.
 */
export const shouldRetryTrpcQuery = (
  failureCount: number,
  error: unknown
): boolean => {
  // Both mean a /logout navigation is already in flight, so a retry only spends
  // requests on a session that is over. Checked ahead of the transient classes
  // because a refresh that died on a dead connection carries a fetch-abort
  // cause, and would otherwise be classified as a recoverable network blip.
  if (isTrpcTokenRefreshError(error) || isTrpcUnauthorizedError(error)) {
    return false;
  }

  if (isTrpcRateLimitError(error)) {
    // Honouring `retryAfterSec` in full is what makes a 429 retry safe, so a
    // wait we are not prepared to sit out is terminal rather than truncated —
    // see MAX_RETRY_AFTER_MS. An absent or unusable hint falls through to
    // bounded backoff, which is the best available guess.
    const retryAfterMs = getTrpcRetryAfterMs(error);
    if (retryAfterMs !== undefined && retryAfterMs > MAX_RETRY_AFTER_MS) {
      return false;
    }

    return failureCount < RATE_LIMIT_RETRY_COUNT;
  }

  if (
    isTrpcTransientInfraError(error) ||
    isTrpcFetchAbortError(error) ||
    isTrpcAuthNotReadyError(error)
  ) {
    return failureCount < DEFAULT_QUERY_RETRY_COUNT;
  }

  return false;
};

/**
 * Default `retryDelay` for every query, paired with `shouldRetryTrpcQuery`
 * above — this only ever runs for a failure that one classified as transient.
 *
 * A 429 waits out the limiter's own `retryAfterSec` plus up to a second of
 * jitter, so a page fanning out 64 requests into the limit does not send all 64
 * retries in the same millisecond. Everything else uses jittered exponential
 * backoff.
 *
 * `failureCount` is the SAME 0-based count `shouldRetryTrpcQuery` is given —
 * query-core calls `retryDelay(failureCount, error)` and `retry(failureCount,
 * error)` on adjacent lines, both before it increments (`query-core/retryer.ts`,
 * and its own `defaultRetryDelay` yields the documented 1s first retry from a
 * count of 0). So the first failure is attempt 0 in both, and the exponent is
 * the count itself.
 */
export const trpcQueryRetryDelayMs = (
  failureCount: number,
  error: unknown
): number => {
  if (!isTrpcRateLimitError(error)) {
    return computeBackoffMs(failureCount);
  }

  const waitMs = getTrpcRetryAfterMs(error) ?? computeBackoffMs(failureCount);

  return waitMs + Math.round(Math.random() * RATE_LIMIT_JITTER_MS);
};
